import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Row, Col, Empty, Spin, Input, Select } from 'antd'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'

import {
    getInspirationGalleryData,
    clearGalleryList,
} from '../store/inspirationGallery/inspiration-gallery-slice'
import { getInspirationCategorydata } from '../store/category/category-slice'
import InspirationCard from '../Inspiration-card'
import InspirationSkeletonCard from '../inspiration-skeleton-card'
import BulkAddInspirationGallery from './inspiration-gallery-bulk-add-view'
import { MarketXToolbar } from '../../marketx/MarketXToolbar'
import { PRIVATE_ROUTES } from '../../../routes'
import styles from '../inspiration.module.css'
import '../css/inspiration-gallery-layout.css'

const PAGE_LIMIT = 24

const Q_TYPE = 'type'
const Q_CATEGORY = 'category'
const Q_SEARCH = 'q'

const parsePlatformFromQuery = (value) => {
    const v = String(value ?? '')
        .trim()
        .toLowerCase()
    if (v === 'mobile') return 'Mobile'
    return 'Web'
}

const filtersFromSearchParams = (params) => ({
    platform: parsePlatformFromQuery(params.get(Q_TYPE)),
    category: params.get(Q_CATEGORY) || null,
    search: (params.get(Q_SEARCH) || '').trim(),
})

const buildSearchParams = ({ platform, category, search }) => {
    const p = new URLSearchParams()
    if (platform === 'Mobile') p.set(Q_TYPE, 'mobile')
    if (category) p.set(Q_CATEGORY, category)
    if (search) p.set(Q_SEARCH, search)
    return p
}

const InspirationGalleryListView = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const [page, setPage] = useState(0)
    const [searchValue, setSearchValue] = useState('')

    const filters = useMemo(
        () => filtersFromSearchParams(searchParams),
        [searchParams]
    )

    const setFilters = useCallback(
        (update) => {
            setSearchParams(
                (prev) => {
                    const cur = filtersFromSearchParams(prev)
                    const next =
                        typeof update === 'function'
                            ? update(cur)
                            : { ...cur, ...update }
                    return buildSearchParams(next)
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )

    const { isLoading, inspirationGalleryList, isListNext } = useSelector(
        (state) => state.inspirationGallerySlice
    )

    const { categoryList, isLoading: categoryLoading } = useSelector(
        (state) => state.inspirationCategorySlice
    )

    useEffect(() => {
        setSearchValue(filters.search || '')
    }, [filters.search])

    useEffect(() => {
        const delay = setTimeout(() => {
            const next = (searchValue || '').trim()
            setFilters((prev) => {
                const cur = (prev.search || '').trim()
                if (cur === next) return prev
                return { ...prev, search: next }
            })
        }, 500)

        return () => clearTimeout(delay)
    }, [searchValue, setFilters])

    useEffect(() => {
        dispatch(clearGalleryList())
        setPage(0)
        dispatch(
            getInspirationGalleryData({
                skip: 0,
                limit: PAGE_LIMIT,
                filter: {
                    platform: filters.platform,
                    ...(filters.category && { category: filters.category }),
                },
                search: filters.search
                    ? { projectTitle: filters.search }
                    : {},
            })
        )
    }, [dispatch, filters.platform, filters.category, filters.search])

    const loadMore = () => {
        if (!isListNext || isLoading) return
        const nextPage = page + PAGE_LIMIT
        setPage(nextPage)
        dispatch(
            getInspirationGalleryData({
                skip: nextPage,
                limit: PAGE_LIMIT,
                filter: {
                    platform: filters.platform,
                    ...(filters.category && { category: filters.category }),
                },
                search: filters.search
                    ? { projectTitle: filters.search }
                    : {},
            })
        )
    }

    const handleClickView = (item) =>
        navigate(PRIVATE_ROUTES.inspirationGallery.view(item._id))

    return (
        <div className={styles.page}>
            <MarketXToolbar>
                <div className={styles.tabToolbarInline}>
                    <div className={styles.controls}>
                        <div className={styles.searchWrap}>
                            <Input
                                allowClear
                                size="large"
                                placeholder="Search inspiration"
                                className={styles.search}
                                prefix={
                                    <Icon
                                        icon="mdi:magnify"
                                        width={18}
                                        height={18}
                                    />
                                }
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                            />
                        </div>
                        <Select
                            size="large"
                            className={styles.categorySelect}
                            placeholder="Platform"
                            value={filters.platform}
                            onChange={(value) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    platform: value || 'Web',
                                }))
                            }
                            options={[
                                { value: 'Web', label: 'Web' },
                                { value: 'Mobile', label: 'Mobile' },
                            ]}
                        />
                        <Select
                            size="large"
                            className={styles.categorySelect}
                            placeholder="Category"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            loading={categoryLoading}
                            value={filters.category}
                            onChange={(value) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    category: value || null,
                                }))
                            }
                            onDropdownVisibleChange={(open) => {
                                if (open) dispatch(getInspirationCategorydata())
                            }}
                            options={categoryList?.map((item) => ({
                                value: item._id,
                                label: item.name,
                            }))}
                        />
                        <BulkAddInspirationGallery />
                    </div>
                </div>
            </MarketXToolbar>

            <div
                id="inspiration-gallery-scroll"
                className={styles.content}
            >
                <InfiniteScroll
                    dataLength={inspirationGalleryList.length}
                    next={loadMore}
                    hasMore={isListNext}
                    scrollableTarget="inspiration-gallery-scroll"
                    loader={
                        <div
                            className={styles.loaderRow}
                            role="status"
                            aria-live="polite"
                        >
                            <Spin size="small" />
                            <span>Loading more…</span>
                        </div>
                    }
                >
                    <Row gutter={[20, 20]} className={styles.grid}>
                        {isLoading && page === 0 ? (
                            Array.from({ length: 8 }).map((_, index) => (
                                <Col
                                    key={index}
                                    xs={24}
                                    sm={12}
                                    lg={8}
                                    xl={6}
                                >
                                    <InspirationSkeletonCard />
                                </Col>
                            ))
                        ) : inspirationGalleryList.length === 0 ? (
                            <Col span={24}>
                                <div className={styles.emptyWrap}>
                                    <Empty
                                        description="No inspiration matches your filters yet"
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                </div>
                            </Col>
                        ) : (
                            inspirationGalleryList.map((item) => (
                                <Col
                                    key={item._id}
                                    xs={24}
                                    sm={12}
                                    lg={8}
                                    xl={6}
                                >
                                    <InspirationCard
                                        item={item}
                                        onOpen={handleClickView}
                                    />
                                </Col>
                            ))
                        )}
                    </Row>
                </InfiniteScroll>
            </div>
        </div>
    )
}

export default InspirationGalleryListView
